// The field bar: Studio on the phone, on the road. Big buttons at the bottom of a Marine phone screen:
// Today (her day), Talk (live voice with VERA, hands-free; VERA can change the CRM by voice), Snap (the phone camera,
// filed on a job), Note (typed or dictated, added to a job's notes), Attach (photos and videos already on the phone:
// one photo is filed on a job like Snap, several or videos go to From the field) and Location. Photos and notes wait on the phone when there
// is no signal and go when it's back (see fieldQueue). Only on narrow screens; the desktop is unchanged.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon, useStudio } from "./ui";
import { VoiceMode } from "./VoiceMode";
import { saveConvo, serverHistory } from "../lib/history";
import { locationOn, setLocationOn } from "../lib/location";
import { DictateButton } from "./DictateButton";
import { cpFetch } from "../lib/controlPlane";
import { crm, titleOf, CLOSED, type CrmHome, type CrmRecord, type CrmType } from "../lib/crm";
import { flush, indexedDbStore, memoryStore, notesField, photosField, type FieldItem, type QueueStore } from "../lib/fieldQueue";
import { CONTROL_PLANE_WORKSPACE } from "../config/model";
import { T } from "../lib/i18n";

const store: QueueStore = indexedDbStore() ?? memoryStore();
const MAX_SIDE = 2048;
/** The server's limit for videos (FieldPhotos on the control plane). */
const MAX_VIDEO = 100 * 1024 * 1024;

/** A phone photo as a JPEG data URL, at most 2048 px on its longest side (the field photos take JPEG up to 8 MB). */
async function toJpeg(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => { const i = new Image(); i.onload = () => resolve(i); i.onerror = reject; i.src = url; });
    const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.naturalWidth * scale); canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.85);
  } finally { URL.revokeObjectURL(url); }
}

/** A talk on the road is kept like any conversation (one a day), so it's in her history and VERA can recall it. */
function keepTalk(all: { who: "you" | "vera"; text: string }[]) {
  const lines = all.filter((l) => l.text.trim());
  if (!lines.length || !serverHistory()) return;
  const now = new Date();
  const id = `talk-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}-${now.getTime()}`;
  const title = `${T("On the road")}, ${now.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  void saveConvo({ id, title, scope: "marine", group: "", updated: now.getTime() },
    lines.map((l) => ({ role: l.who === "you" ? "user" as const : "vera" as const, text: l.text, at: now.getTime() }))).catch(() => undefined);
}

export function FieldBar() {
  const { go, getToken, toast } = useStudio();
  const [home, setHome] = useState<CrmHome | null>(null);
  const [jobs, setJobs] = useState<CrmRecord[]>([]);
  const [waiting, setWaiting] = useState(0);
  const [talk, setTalk] = useState(false);
  const [sheet, setSheet] = useState<null | { kind: "photo" | "note"; dataUrl?: string }>(null);
  const [text, setText] = useState("");
  const [jobId, setJobId] = useState("");
  const camera = useRef<HTMLInputElement>(null);
  const library = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const workspaceId = CONTROL_PLANE_WORKSPACE.marine!.workspaceId;

  const jobsType: CrmType | undefined = useMemo(() => home?.schema.types.find((t) => t.id === "jobs"), [home]);
  const open = useMemo(() => {
    const status = jobsType?.statusField;
    return jobs.filter((j) => !status || !CLOSED.test(String(j.values[status] ?? "")));
  }, [jobs, jobsType]);

  const load = useCallback(async () => {
    try {
      const h = await crm.home(getToken);
      setHome(h);
      if (h.schema.types.some((t) => t.id === "jobs")) setJobs(await crm.list(getToken, "jobs"));
    } catch { /* offline: the bar still saves to the phone */ }
  }, [getToken]);

  const send = useCallback(async () => {
    if (!home) { setWaiting((await store.all()).length); return; }
    const r = await flush(store, {
      types: home.schema.types,
      upload: async (dataUrl, note) => (await cpFetch<{ id: string }>(getToken, "/v1/field/photos", { method: "POST", body: JSON.stringify({ workspaceId, dataUrl, note }) })).id,
      get: (type, id) => crm.get(getToken, type, id),
      update: (type, id, version, values) => crm.update(getToken, type, id, version, values),
    });
    setWaiting(r.waiting);
    if (r.sent) toast(r.sent === 1 ? T("Sent to the job.") : `${r.sent} ${T("sent to their jobs.")}`);
  }, [home, getToken, toast, workspaceId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { void send(); }, [send]);
  useEffect(() => {
    const again = () => { void load().then(() => send()); };
    window.addEventListener("online", again);
    const timer = window.setInterval(() => { if (navigator.onLine) void send(); }, 30_000);
    return () => { window.removeEventListener("online", again); window.clearInterval(timer); };
  }, [load, send]);

  const save = async () => {
    const job = open.find((j) => j.id === jobId) ?? jobs.find((j) => j.id === jobId);
    if (!sheet || !job || !jobsType) return;
    if (sheet.kind === "note" && !text.trim()) return;
    const item: FieldItem = {
      id: crypto.randomUUID(), kind: sheet.kind, jobType: jobsType.id, jobId: job.id, jobTitle: titleOf(jobsType, job),
      at: Date.now(), dataUrl: sheet.dataUrl, text: text.trim() || undefined, tries: 0,
    };
    await store.put(item);
    setSheet(null); setText(""); setJobId("");
    toast(navigator.onLine ? T("Sending…") : T("Saved on your phone. It goes when you have signal."));
    await send();
  };

  const canFile = Boolean(jobsType && photosField(jobsType) && notesField(jobsType));

  // Attach: one photo is filed on a job, like Snap. Several, or videos, go to From the field (Studio), where VERA can
  // file them later.
  const attach = async (files: File[]) => {
    const photos = files.filter((f) => f.type.startsWith("image/"));
    const videos = files.filter((f) => f.type.startsWith("video/"));
    if (photos.length === 1 && !videos.length && canFile) {
      try { setSheet({ kind: "photo", dataUrl: await toJpeg(photos[0]!) }); } catch { toast(T("That photo couldn't be read. Try again.")); }
      return;
    }
    setUploading(true);
    let saved = 0;
    const failed: string[] = [];
    for (const f of [...photos, ...videos]) {
      try {
        if (f.type.startsWith("video/")) {
          if (f.size > MAX_VIDEO) { failed.push(`${f.name} (${T("larger than 100 MB")})`); continue; }
          await cpFetch<{ id: string }>(getToken, `/v1/field/videos?workspaceId=${encodeURIComponent(workspaceId)}&note=${encodeURIComponent(T("Uploaded from the phone"))}`,
            { method: "POST", body: f, headers: { "Content-Type": f.type === "video/quicktime" ? "video/quicktime" : "video/mp4" } });
        } else {
          await cpFetch<{ id: string }>(getToken, "/v1/field/photos", { method: "POST", body: JSON.stringify({ workspaceId, dataUrl: await toJpeg(f), note: T("Uploaded from the phone") }) });
        }
        saved++;
      } catch { failed.push(f.name); }
    }
    setUploading(false);
    if (saved) toast(`${saved} ${T("saved to From the field.")}`);
    if (failed.length) toast(`${T("Not uploaded:")} ${failed.join(", ")}`);
  };

  // Location: off until she switches it on; then VERA knows where she is for the drive, weather and "nearby".
  const [here, setHere] = useState(locationOn());
  const toggleHere = async () => {
    if (here) { await setLocationOn(false); setHere(false); toast(T("Location is off. VERA no longer knows where you are.")); return; }
    if (await setLocationOn(true)) { setHere(true); toast(T("Location is on. VERA knows where you are when you ask her something.")); }
    else toast(T("Your phone didn't share the location. Allow it for this site in your phone's settings, then try again."));
  };

  return (
    <>
      <nav className="fieldbar" aria-label={T("On the road")}>
        <button onClick={() => go("home")}><Icon name="home" size={22} /><span>{T("Today")}</span></button>
        <button className="talk" onClick={() => setTalk(true)}><Icon name="mic" size={26} /><span>{T("Talk")}</span></button>
        <button disabled={!canFile} onClick={() => camera.current?.click()}><Icon name="img" size={22} /><span>{T("Snap")}</span></button>
        <button disabled={!canFile} onClick={() => setSheet({ kind: "note" })}><Icon name="doc" size={22} /><span>{T("Note")}</span></button>
        <button disabled={uploading} onClick={() => library.current?.click()}><Icon name="clip" size={22} /><span>{uploading ? T("Sending…") : T("Attach")}</span></button>
        <button className={here ? "on" : ""} aria-pressed={here} onClick={() => void toggleHere()}><Icon name="globe" size={22} /><span>{T(here ? "Location on" : "Location")}</span></button>
        {waiting > 0 && <span className="fieldwait" role="status">{waiting} {T("waiting")}</span>}
      </nav>
      <input ref={library} type="file" accept="image/*,video/*" multiple hidden
        onChange={(e) => { const files = [...(e.target.files ?? [])]; e.target.value = ""; if (files.length) void attach(files); }} />
      <input ref={camera} type="file" accept="image/*" capture="environment" hidden
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          try { setSheet({ kind: "photo", dataUrl: await toJpeg(file) }); } catch { toast(T("That photo couldn't be read. Try again.")); }
        }} />
      {talk && <div className="fieldtalk"><VoiceMode scope="marine" getToken={getToken} onClose={(lines) => { setTalk(false); keepTalk(lines); }} /></div>}
      {sheet && (
        <div className="fieldsheet" role="dialog" aria-modal="true" aria-label={sheet.kind === "photo" ? T("File the photo") : T("Add a note")}>
          <div className="card">
            <h2>{sheet.kind === "photo" ? T("File the photo on a job") : T("Add a note to a job")}</h2>
            {sheet.dataUrl && <img className="fieldpreview" src={sheet.dataUrl} alt="" />}
            <label className="small">{T("Job")}
              <select value={jobId} onChange={(e) => setJobId(e.target.value)}>
                <option value="">{T("Choose the job…")}</option>
                {open.map((j) => <option key={j.id} value={j.id}>{jobsType ? titleOf(jobsType, j) : j.id}</option>)}
              </select>
            </label>
            <label className="small">{sheet.kind === "photo" ? T("Caption (optional)") : T("Note")}
              <textarea rows={sheet.kind === "photo" ? 2 : 5} value={text} onChange={(e) => setText(e.target.value)}
                placeholder={sheet.kind === "photo" ? T("What it shows") : T("What you saw, measured or need: materials, next steps")} />
            </label>
            <div className="row between">
              <DictateButton onText={(t) => setText((v) => (v ? `${v} ${t}` : t))} />
              <div className="row">
                <button className="btn" onClick={() => { setSheet(null); setText(""); setJobId(""); }}>{T("Cancel")}</button>
                <button className="btn primary" disabled={!jobId || (sheet.kind === "note" && !text.trim())} onClick={() => void save()}>{T("Save")}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
